
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  OnInit,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import { ButtonInstance, JepLayer } from '@jeppesen-foreflight/jmkw-types';
import { ButtonComponent } from '../../base';
import { AbstractButton } from '../../typings';
import { catchError } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import type { ResourceHandle } from "@arcgis/core/core/Handles";

@Component({
  selector: 'jmkw-button-basic',
  templateUrl: './button-basic.component.html',
  styleUrls: ['./button-basic.component.scss'],
  standalone: true,
  imports: [ButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BasicButtonComponent
  extends AbstractButton
  implements OnInit, OnDestroy
{
  // Interface properties that can be passed as inputs
  @Input() iconName?: string;
  @Input() buttonText?: string;
  @Input() tooltipText?: string;
  @Input() automationKey?: string;

  // BasicButton-specific @Input properties
  @Input() stateFn?: (button: ButtonInstance, layer: JepLayer) => void;
  @Input() submit?: (button: ButtonInstance, e: MouseEvent) => void;

  @ViewChild('button', { static: true })
  buttonRef!: ElementRef<ButtonComponent>;

  private layerHandle?: ResourceHandle;
  private readonly _destroyRef = inject(DestroyRef);

  ngOnInit() {
    if (this.id && this.stateFn) {
      this.setupLayerStateWatch();
    } else if (this.active !== undefined) {
      this.$active.set(this.active);
    }
  }

  private setupLayerStateWatch() {
    this.toolkitApiService
      .onLayerReady$(this.id)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        catchError((error) => {
          console.error(
            `Error setting up layer state watch for button ${this.id}:`,
            error
          );
          return EMPTY;
        })
      )
      .subscribe((layer) => {
        try {
          // Execute stateFn to set initial state
          if (this.stateFn) {
            this.stateFn(this as ButtonInstance, layer);
          }
        } catch (error) {
          console.error(
            `Error executing initial stateFn for button ${this.id}:`,
            error
          );
        }

        try {
          this.layerHandle = reactiveUtils.watch(
            () => layer.visible,
            () => {
              try {
                // Execute stateFn to update state when layer changes
                if (this.stateFn) {
                  this.stateFn(this as ButtonInstance, layer);
                }
              } catch (error) {
                console.error(
                  `Error executing stateFn for button ${this.id} on layer visibility change:`,
                  error
                );
              }
            }
          );
        } catch (error) {
          console.error(
            `Error setting up layer visibility watcher for button ${this.id}:`,
            error
          );
        }
      });
  }

  ngOnDestroy() {
    this.layerHandle?.remove();
  }

  onClickHandler(event: MouseEvent, button: ButtonComponent): void {
    if (this.submit) {
      this.submit(this as unknown as ButtonInstance, event);

      if (this.id && this.stateFn) {
        const layer = this.toolkitApiService.getLayerById(this.id);
        if (layer) {
          try {
            this.stateFn(this as ButtonInstance, layer);
          } catch (error) {
            console.error(
              `Error executing stateFn for button ${this.id} on click:`,
              error
            );
          }
        }
      }
    }

    this.toolkitApiService.dispatchEvent({
      sourceId: 'basicButton',
      source: this,
      event: event
    });
  }
}
